import { useAtom, useAtomValue, useSetAtom, useStore, type PrimitiveAtom } from "jotai";
import {
    changeDraftStepTypeAtom,
    createDraftAtomEffect,
    draftStepAtom,
    isCreatingStepAtom,
    newStepAtom,
    removeStepAtom,
    stepIndexBeingEditedAtom,
    stepListContainerAtom,
    stepNodeYPositionAtom,
    upsertDraftStepIntoStepsAtom,
} from "@/routes/automations/automation-details-page/state";
import type React from "react";
import { Select, SelectItem, SelectContent, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import type { WorkflowEditFormStepType } from "@/components/automations/automation-edit-form-data";
import { IconConsoleSimple } from "central-icons/IconConsoleSimple";
import IconBubbleText6 from "central-icons/IconBubbleText6";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { ShellInput } from "@/components/flexkit/ShellInput";
import { useEffect, useLayoutEffect, useRef, useState, type FC } from "react";
import { Button } from "@/components/flexkit/Button";
import { IconPullRequest } from "central-icons/IconPullRequest";
import IconChecklist from "central-icons/IconChecklist";
import type { DraftStep } from "@/routes/automations/automation-details-page/state";
import { AutomationPullRequestStepEditor } from "@/routes/automations/automation-edit-page/AutomationPullRequestStepEditor";
import { AutomationReportStepEditor } from "@/routes/automations/automation-edit-page/AutomationReportStepEditor";
import { useFlagAutomationsStepReportEnabled } from "@/hooks/use-flag";

const AutomationStepEditArea: FC = () => {
    useAtom(createDraftAtomEffect);

    const draftStep = useAtomValue(draftStepAtom);
    const isCreatingStep = useAtomValue(isCreatingStepAtom);
    const isVisible = draftStep !== null;

    return (
        <div
            className="relative min-h-full transition-all duration-300 ease-in-out"
            style={{
                flex: isVisible ? 1 : 0,
                width: isVisible ? "auto" : 0,
                opacity: isVisible ? 1 : 0,
            }}
        >
            {draftStep && (
                <StepEditorPositioner>
                    <AutomationStepEditor create={isCreatingStep} draft={draftStep} />
                </StepEditorPositioner>
            )}
        </div>
    );
};

const StepEditorPositioner: FC<{ children: React.ReactNode }> = ({ children }) => {
    const expectedYPosition = useAtomValue(stepNodeYPositionAtom);
    const [yPosition, setYPosition] = useState<number | null>(null);
    const [listContainerHeight, setListContainerHeight] = useState<number | null>(null);
    const [editorHeight, setEditorHeight] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const stepListContainer = useAtomValue(stepListContainerAtom);
    const updateYTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useLayoutEffect(() => {
        // calculate the actual y position
        // the expected y position may cause the editor to extend beyond the bottom of the parent
        // therefore it needs to be clamped to the max y position
        if (stepListContainer && listContainerHeight !== null && editorHeight !== null) {
            const parentStyles = window.getComputedStyle(stepListContainer);
            const paddingBottom = Number.parseFloat(parentStyles.paddingBottom);
            const maxY = listContainerHeight - paddingBottom - editorHeight - 16;
            // this timeout is to prevent brief flickering of the editor position
            // this helps stabilize the editor position when the layout is changing rapidly
            if (updateYTimeout.current) {
                clearTimeout(updateYTimeout.current);
            }
            updateYTimeout.current = setTimeout(() => {
                setYPosition(Math.min(expectedYPosition ?? maxY, maxY));
            }, 1);
        }
    }, [expectedYPosition, listContainerHeight, editorHeight, stepListContainer]);

    useEffect(() => {
        if (stepListContainer) {
            const observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    if (entry.target === stepListContainer) {
                        setListContainerHeight(entry.contentRect.height);
                    } else if (entry.target === containerRef.current) {
                        setEditorHeight(entry.contentRect.height);
                    }
                }
            });
            observer.observe(stepListContainer);
            if (containerRef.current) {
                observer.observe(containerRef.current);
            }
            return () => observer.disconnect();
        }
    }, [stepListContainer]);

    const isVisible = yPosition !== null;

    return (
        <div
            ref={containerRef}
            // translate up by 1px otherwise it is off by 1px
            // probably because of the border
            className="absolute w-full transition-all duration-300 ease-in-out"
            style={{
                top: yPosition ? `${yPosition}px` : "0px",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateX(0) translateY(-1px)" : "translateX(8px) translateY(-1px)",
                pointerEvents: isVisible ? "auto" : "none",
            }}
        >
            {children}
        </div>
    );
};

const AutomationStepEditor: FC<{ create: boolean; draft: DraftStep }> = ({ create, draft }) => {
    const removeStep = useSetAtom(removeStepAtom);
    const store = useStore();
    const setNewStep = useSetAtom(newStepAtom);
    const setStepIndexBeingEdited = useSetAtom(stepIndexBeingEditedAtom);
    const setStepNodeYPosition = useSetAtom(stepNodeYPositionAtom);
    const upsertDraftStepIntoSteps = useSetAtom(upsertDraftStepIntoStepsAtom);

    const saveEditorChanges = () => {
        upsertDraftStepIntoSteps();
    };

    const cancelEditorChanges = () => {
        setNewStep(null);
        setStepIndexBeingEdited(null);
        setStepNodeYPosition(null);
    };

    const removeCurrentStep = () => {
        const index = store.get(stepIndexBeingEditedAtom);
        if (index !== null) {
            removeStep(index);
        }
    };

    let contentEditor: React.ReactNode | null;
    switch (draft.type) {
        case "command":
            contentEditor = <StepCommandEditor contentAtom={draft.content} />;
            break;
        case "prompt":
            contentEditor = <StepPromptEditor contentAtom={draft.content} />;
            break;
        case "pr":
            contentEditor = <AutomationPullRequestStepEditor draft={draft} />;
            break;
        case "report":
            contentEditor = <AutomationReportStepEditor draft={draft} />;
            break;
    }

    return (
        <div className="flex max-w-[720px] flex-col gap-3 rounded-xl border border-border-brand-subtle bg-surface-primary p-4 shadow">
            <StepTypeSelect draft={draft} />
            {contentEditor}
            <div className="flex justify-end gap-2">
                {create ? null : (
                    <div className="flex-1">
                        <Button variant="ghost" onClick={removeCurrentStep} data-tracking-id="remove-step">
                            Remove step
                        </Button>
                    </div>
                )}
                <Button variant="outline" onClick={cancelEditorChanges} data-tracking-id="cancel">
                    Cancel
                </Button>
                <Button variant="primary" onClick={saveEditorChanges} data-tracking-id="update">
                    {create ? "Add" : "Update"}
                </Button>
            </div>
        </div>
    );
};

const StepTypeSelect: FC<{ draft: DraftStep }> = ({ draft }) => {
    const changeDraftStepType = useSetAtom(changeDraftStepTypeAtom);
    const { value: reportStepEnabled } = useFlagAutomationsStepReportEnabled();

    let icon: React.ReactNode | null = null;
    let label: string | null = null;

    switch (draft.type) {
        case "command":
            icon = <IconConsoleSimple size={16} />;
            label = "Shell script";
            break;
        case "prompt":
            icon = <IconBubbleText6 size={16} />;
            label = "Prompt";
            break;
        case "pr":
            icon = <IconPullRequest size={16} />;
            label = "Pull request";
            break;
        case "report":
            icon = <IconChecklist size={16} />;
            label = "Report";
            break;
    }

    const updateStepType = (type: WorkflowEditFormStepType) => {
        changeDraftStepType(type);
    };

    return (
        <Select value={draft.type} onValueChange={(value) => updateStepType(value as WorkflowEditFormStepType)}>
            <SelectTrigger loading={false} className="w-fit">
                <SelectValue placeholder="Select step type">
                    <div className="flex items-center gap-2">
                        {icon}
                        <span>{label}</span>
                    </div>
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="command">
                    <div className="flex items-center gap-2 text-base">
                        <IconConsoleSimple size={16} />
                        <span>Shell script</span>
                    </div>
                </SelectItem>
                <SelectItem value="prompt">
                    <div className="flex items-center gap-2 text-base">
                        <IconBubbleText6 size={16} />
                        <span>Prompt</span>
                    </div>
                </SelectItem>
                <SelectItem value="pr">
                    <div className="flex items-center gap-2 text-base">
                        <IconPullRequest size={16} />
                        <span>Pull request</span>
                    </div>
                </SelectItem>
                {reportStepEnabled && (
                    <SelectItem value="report">
                        <div className="flex items-center gap-2 text-base">
                            <IconChecklist size={16} />
                            <span>Report</span>
                        </div>
                    </SelectItem>
                )}
            </SelectContent>
        </Select>
    );
};

const StepPromptEditor: FC<{ contentAtom: PrimitiveAtom<string> }> = ({ contentAtom }) => {
    const [value, setValue] = useAtom(contentAtom);
    return (
        <div className="rounded-lg bg-surface-muted p-1.5">
            <Textarea
                minHeight={120}
                maxHeight={400}
                className="max-w-full resize-none rounded-md border border-border-base bg-surface-primary p-3 text-base shadow-sm outline-none focus:border-border-strong focus:ring-0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter the prompt for this step"
            />
        </div>
    );
};

const StepCommandEditor: FC<{ contentAtom: PrimitiveAtom<string> }> = ({ contentAtom }) => {
    const [value, setValue] = useAtom(contentAtom);

    return (
        <ShellInput
            value={value}
            onChange={setValue}
            placeholder="Enter shell commands"
            minHeight={120}
            maxHeight={400}
        />
    );
};

export default AutomationStepEditArea;
